import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, MessageSquare, FileSearch, Clock, Globe, Heart, ArrowRight, Check, Star, Play, X } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Home = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState(false);

  const moduleVideos = [
    {
      id: 'intro',
      title: 'Introduction',
      description: 'Learn how Mattoo Consulting can help you succeed',
      thumbnail: 'https://images.pexels.com/photos/7792770/pexels-photo-7792770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      videoUrl: 'https://player.vimeo.com/video/1178714411',
      isVimeo: true
    },
    {
      id: 'role-clarity',
      title: 'Role Clarity',
      description: 'Know exactly which roles to target',
      thumbnail: 'https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=400',
      videoUrl: 'https://player.vimeo.com/video/1178714341',
      isVimeo: true
    },
    {
      id: 'cv-linkedin',
      title: 'CV and LinkedIn',
      description: 'Get past ATS and in front of recruiters',
      thumbnail: 'https://images.pexels.com/photos/3184299/pexels-photo-3184299.jpeg?auto=compress&cs=tinysrgb&w=400',
      videoUrl: 'https://player.vimeo.com/video/1178714233',
      isVimeo: true
    },
    {
      id: 'interview-job-hunt',
      title: 'Interview & Job Hunt',
      description: 'Prepare and access hidden opportunities',
      thumbnail: 'https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=400',
      videoUrl: 'https://player.vimeo.com/video/1178714168',
      isVimeo: true
    }
  ];

  const struggles = [
    {
      icon: FileText,
      title: 'CVs Lost in the Crowd',
      description: 'We create laser-focused CVs that speak the recruiter\'s language and pass ATS filters.'
    },
    {
      icon: MessageSquare,
      title: 'Interviews That Don\'t Convert',
      description: 'We coach you to own your story, answer with clarity, and leave a lasting impression.'
    },
    {
      icon: Clock,
      title: 'No Feedback or Silence',
      description: 'We audit your applications and simulate real interviews with detailed feedback.'
    }
  ];

  const testimonials = [
    {
      name: 'Varun S',
      title: 'From Frustration to Two Job Offers',
      quote: 'I was struggling for weeks until I reached out to Anant. His positivity and mental conditioning kept me going, and his detailed CV feedback made me realise I was in the right hands. The mock interviews transformed how I articulated my answers, and within weeks I had two job offers.'
    },
    {
      name: 'Supreet S',
      title: 'Confidence, Clarity, and the Right Role',
      quote: 'After months of job hunting without success, Anant helped me refine my CV, improve my communication, and create a clear job search plan. His interview coaching completely changed my approach, and I landed a role perfectly suited to my skills.'
    },
    {
      name: 'Ankit B',
      title: 'First Job in Australia Secured',
      quote: 'Anant played a key role in helping me land my first job in Australia. He expertly translated my skills into a market-ready CV and optimised my job search strategy. The results spoke for themselves and I secured the role I was aiming for.'
    },
    {
      name: 'Abishek S',
      title: 'From Zero Calls to Real Opportunities',
      quote: 'I was getting no interview calls and felt stuck until I started working with Anant. His guidance on my CV, LinkedIn, and interview prep gave me clarity and confidence. Within weeks, the opportunities started coming through, and I landed a role.'
    },
    {
      name: 'Ana S',
      title: 'From Fresh Graduate to a New Role',
      quote: 'I was a fresh graduate struggling to figure out the right roles to apply for. Anant helped me gain clarity, rebuild my CV and LinkedIn, and approach my job search with confidence. A few weeks later, the interview calls started coming in, and I landed a role I\'m genuinely excited about.'
    },
    {
      name: 'Naveen S',
      title: 'Moving to a Senior Role',
      quote: 'Anant provided a structured and organised approach that made my job search much easier. He helped me create a clear, repeatable process and turned an overwhelming experience into a smooth and rewarding one. Highly recommend his services.'
    }
  ];

  const companyLogos = [
    'UniSuper', 'PlanIt', 'RMIT', 'IAG', 'Aware Super', 
    'Dept of Justice VIC', 'Bupa', 'DFFH', 'NBN', 'RACQ', 
    'QSuper', 'Monash University', 'VicSuper', 'NAB', 'EY', 'VicRoads'
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Mattoo Consulting | Career Coaching for Skilled Migrants & Overseas Professionals in Australia</title>
        <meta name="description" content="Built for skilled migrants and overseas professionals turning international experience into Australian job offers. CV coaching, interview prep, and job search strategy." />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-cream via-brand-mist/50 to-brand-pale/30" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-brand-dark mb-6">
                Land Your Ideal Role in Australia in Weeks, Not Months
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                Built for skilled migrants and overseas professionals turning international experience into Australian job offers.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/book-a-call"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center justify-center gap-2 bg-brand-primary hover:bg-brand-medium text-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Book a Free Discovery Call <ArrowRight size={20} />
                </Link>
                <Link
                  to="/packages"
                  onClick={() => window.scrollTo(0, 0)}
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-mist text-brand-primary border-2 border-brand-primary rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
                >
                  View Packages
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works - Video Modules */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="inline-block text-sm font-medium tracking-wide uppercase text-brand-medium mb-4">
              How It Works
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-brand-dark mb-4">
              A Proven System to Get You Hired
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Watch these short videos to see how we help you succeed
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {moduleVideos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => setActiveVideo(video)}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group-hover:-translate-y-1">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6">
                    <div className="text-white">
                      <h3 className="font-semibold text-lg mb-1">{video.title}</h3>
                      <p className="text-sm text-white/80">{video.description}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center group-hover:bg-brand-primary group-hover:scale-110 transition-all duration-300">
                      <Play className="w-8 h-8 text-brand-primary group-hover:text-white ml-1" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setActiveVideo(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative w-full max-w-4xl bg-white rounded-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setActiveVideo(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-brand-mist transition-colors"
              >
                <X className="w-6 h-6 text-brand-primary" />
              </button>
              <div className="aspect-video">
                {activeVideo.isVimeo ? (
                  <iframe
                    src={`${activeVideo.videoUrl}?autoplay=1`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={activeVideo.title}
                  />
                ) : (
                  <video
                    className="w-full h-full"
                    controls
                    autoPlay
                    onLoadStart={() => setVideoLoading(true)}
                    onCanPlay={() => setVideoLoading(false)}
                    onError={() => setVideoError(true)}
                  >
                    <source src={activeVideo.videoUrl} type="video/mp4" />
                  </video>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Struggles Section */}
      <section className="py-20 md:py-32 bg-brand-cream">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-brand-dark mb-4">
              We Understand Your Struggles
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              And we know exactly how to fix them
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {struggles.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl border border-brand-pale/50 hover:border-brand-light/50 transition-all duration-300 hover:shadow-lg"
              >
                <div className="w-12 h-12 bg-brand-mist rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-6 h-6 text-brand-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-brand-dark mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Me Section */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_mattooconsulting-v2/artifacts/8l76lm6p_Headshots.png"
                alt="Anant Mattoo - Career Coach"
                className="rounded-3xl shadow-xl"
              />
            </motion.div>
            <motion.div {...fadeInUp}>
              <span className="inline-block text-sm font-medium tracking-wide uppercase text-brand-medium mb-4">
                My Story
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-brand-dark mb-6">
                Why Me?
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  When I moved to Australia over a decade ago, I brought with me several years of international IT experience and a suitcase full of ambition. But stepping into a new job market felt like starting from scratch. I quickly realised that even seasoned professionals can feel lost.
                </p>
                <p>
                  That experience reshaped me. Through relentless effort, I found my footing and then made it my mission to help others. Since then, I've guided over 50 professionals from diverse backgrounds to land roles that truly fit.
                </p>
                <p>
                  I know what it's like to feel stuck. That's why I coach with empathy, strategy, and a deep understanding of what actually works in today's market. Together, we'll uncover your strengths, sharpen your goals, and make the subtle shifts that lead to big breakthroughs.
                </p>
              </div>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[1,2,3].map(i => (
                    <div key={i} className="w-10 h-10 rounded-full bg-brand-mist border-2 border-white flex items-center justify-center">
                      <Check className="w-5 h-5 text-brand-primary" />
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  <span className="font-semibold text-brand-dark">50+</span> professionals placed
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Keep 3 */}
      <section className="py-20 md:py-32 bg-brand-primary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-white mb-4">
              Real Results from Real Clients
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-3xl"
              >
                <div className="flex gap-1 mb-4">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-5 h-5 fill-brand-sand text-brand-sand" />
                  ))}
                </div>
                <h3 className="font-serif text-xl font-semibold text-brand-dark mb-3">
                  {item.title}
                </h3>
                <p className="text-muted-foreground italic mb-4">
                  &ldquo;{item.quote}&rdquo;
                </p>
                <p className="font-semibold text-brand-primary">
                  {item.name}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Logos Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl font-semibold tracking-tight text-brand-dark mb-4">
              Clients Placed At
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-6"
          >
            {companyLogos.map((company) => (
              <div
                key={company}
                className="flex items-center justify-center p-4 bg-brand-mist/30 rounded-xl hover:bg-brand-mist/50 transition-colors"
              >
                <span className="text-sm font-medium text-brand-dark text-center">
                  {company}
                </span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-brand-primary to-brand-medium">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold tracking-tight text-white mb-6">
              Ready to Transform Your Job Search?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              A free 30-minute call. No pitch, no pressure — just an honest look at where you are, what's holding you back, and whether (and which) package would actually help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-a-call"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-brand-mist text-brand-primary rounded-full px-8 py-4 text-lg font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                Book a Free Discovery Call <ArrowRight size={20} />
              </Link>
              <Link
                to="/packages"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white border-2 border-white rounded-full px-8 py-4 text-lg font-medium transition-all duration-300"
              >
                View Packages
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
